'use client';

import { toTitleCase } from '@/utils/toTitleCase';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { format } from 'date-fns';
import { detect as detectBrowser } from 'detect-browser';
import { throttle } from 'lodash';
import { useEffect, useReducer, useRef, useState } from 'react';
import { messages } from '../messages';
import type { DataBlocks } from '../types';

export const useData = () => {
  const browser = detectBrowser();
  const [_, forceUpdate] = useReducer((x) => x + 1, 0);
  const [localStorage, setLocalStorage] = useLocalStorage('privacyArticle', {});

  const getHostname = (url: string) => {
    try {
      return new URL(url).hostname;
    } catch {
      return '';
    }
  };

  const [dataBlocks, setDataBlocks] = useState<DataBlocks>({
    ip: {
      title: messages['dataBlock.ip'],
      value: '',
      access: 'any-website',
    },
    city: {
      title: messages['dataBlock.city'],
      value: '',
      access: 'any-website-guess',
    },
    country: {
      title: messages['dataBlock.country'],
      value: '',
      access: 'any-website-guess',
    },
    latAndLong: {
      title: messages['dataBlock.latAndLong'],
      value: '',
      access: 'any-website-guess',
    },
    browserName: {
      title: messages['dataBlock.browserName'],
      value: browser?.name
        ?.split(' ')
        .map((w) => w[0].toUpperCase() + w.substr(1).toLowerCase())
        .join(' '),
      access: 'any-website',
    },
    browserVersion: {
      title: messages['dataBlock.browserVersion'],
      value: browser?.version?.toString(),
      access: 'any-website',
    },
    os: {
      title: messages['dataBlock.os'],
      value: browser?.os?.toString(),
      access: 'any-website',
    },
    time: {
      title: messages['dataBlock.time'],
      value: format(new Date(), 'HH:mm'),
      access: 'any-website',
    },
    date: {
      title: messages['dataBlock.date'],
      value: format(new Date(), 'do MMMM'),
      access: 'any-website',
    },
    timezone: {
      title: messages['dataBlock.timezone'],
      value: new Intl.DateTimeFormat().resolvedOptions().timeZone,
      access: 'any-website',
    },
    deviceOrientation: {
      title: messages['dataBlock.deviceOrientation'],
      value: '',
      access: 'any-website-or-requires-permission',
    },
    touchPosition: {
      title: messages['dataBlock.touchPosition'],
      value: '',
      access: 'any-website',
    },
    mousePosition: {
      title: messages['dataBlock.mousePosition'],
      value: '',
      access: 'any-website',
    },
    referrer: {
      title: messages['dataBlock.referrer'],
      value: localStorage.referrers ? localStorage.referrers[0] : '',
      access: 'any-website',
    },
    searchParams: {
      title: messages['dataBlock.searchParams'],
      value: '',
      access: 'any-website',
    },
    adBlocker: {
      title: messages['dataBlock.adBlocker'],
      value: 'no',
      access: 'any-website',
    },
    batteryChargingStatus: {
      title: 'Batter Charging Status',
      value: undefined,
      access: 'any-website',
    },
    batteryLevel: {
      title: 'Battery Level',
      value: undefined,
      access: 'any-website',
    },
  });

  const [loadingCount, setLoadingCount] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setDataBlocks((previousState) => {
        previousState.time.value = format(new Date(), 'HH:mm');
        return previousState;
      });
    }, 30 * 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    addEventListener('load', () => {
      setDataBlocks((prev) => ({
        ...prev,
        adBlocker: {
          ...prev.adBlocker,
          value:
            typeof window !== 'undefined' && window.canRunAds ? 'no' : 'yes',
        },
      }));
    });
  }, []);

  const geolocationTries = useRef<number>(0);
  const fetchGeolocation = () => {
    if (geolocationTries.current > 3) return;
    geolocationTries.current += 1;

    setLoadingCount((count) => count + 1);

    fetch(
      `https://europe-west3-personal-website-322920.cloudfunctions.net/geolocation`
    )
      .then((response) => response.json())
      .then((data) => {
        const { userIP: lastKnownIP, city, country, cityLatLong } = data;
        const lastKnownCity = city && city !== '?' ? toTitleCase(city) : '';
        const lastKnownCountry = country && country !== '?' ? country : '';
        const lastKnownLatLong = cityLatLong ? cityLatLong.split(',') : [0, 0];

        setLocalStorage((localStorageData) => ({
          ...localStorageData,
          lastKnownIP,
          lastKnownCity,
          lastKnownCountry,
          lastKnownLatLong,
        }));

        setDataBlocks((previousState) => {
          previousState.ip.value = lastKnownIP;
          previousState.city.value = lastKnownCity;
          previousState.country.value = lastKnownCountry;
          previousState.latAndLong = cityLatLong;
          return previousState;
        });

        forceUpdate();

        if (!lastKnownCity || !lastKnownCountry) {
          throw new Error('Failed to get specific location');
        }
      })
      .catch(() => {
        setLoadingCount((count) => count + 1);
        setTimeout(() => {
          setLoadingCount((count) => count - 1);
          fetchGeolocation();
        }, 500);
      })
      .finally(() => {
        setLoadingCount((count) => count - 1);
      });
  };
  useEffect(() => {
    fetchGeolocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetches once on mount
  }, []);

  useEffect(() => {
    setLocalStorage(({ referrers = [], ...data }) => ({
      ...data,
      referrers: [getHostname(document.referrer), ...referrers],
    }));
    getHostname(document.referrer);

    setDataBlocks((previousState) => {
      previousState.searchParams.value = location.search
        .replace(/^\?/, '')
        .split('&')
        .join(', ');
      return previousState;
    });

    const searchParams = location.search
      .replace(/^\?/, '')
      .split('&')
      .map((parameter) => parameter.split('='));

    const availableSearchParamsEntities = {
      amit: ['a', 'amit'],
      zdenek: ['z', 'zdenek'],
      techsquat: ['t', 'techsquat'],
      vladimir: ['v', 'vladimir'],
      theDrinkingChat: ['tdc', 'the-drinking-chat'],
      hanna: ['h', 'hanna'],
      marta: ['m', 'marta'],
    };

    Object.keys(availableSearchParamsEntities).map((key) => {
      const searchParamsLookup =
        availableSearchParamsEntities[
          key as keyof typeof availableSearchParamsEntities
        ];
      const searchParamsEntity = searchParams.find(
        (param) => searchParamsLookup.indexOf(param[0]) !== -1
      );

      if (searchParamsEntity) {
        setLocalStorage(({ referrers: _referrers, ...data }) => ({
          ...data,
          lastSearchParamEntity:
            key as keyof typeof availableSearchParamsEntities,
        }));
      }
    });

    /**
     * Remove search params from url without refreshing page (history state
     * will also disappear)
     */
    setTimeout(() => {
      window.history.replaceState({}, document.title, location.pathname);
    }, 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- records the referrer and search params once on mount
  }, []);

  useEffect(() => {
    const touchMoveHandler = throttle(
      ({ touches, changedTouches }: TouchEvent) => {
        const { clientX, clientY } = (touches || changedTouches)[0];

        setDataBlocks((previousState) => {
          previousState.touchPosition.value = `X=${Math.round(
            clientX
          )} Y=${Math.round(clientY)}`;
          return previousState;
        });
        forceUpdate();
      },
      200
    );

    const mouseMoveHandler = throttle(({ clientX, clientY }: MouseEvent) => {
      setDataBlocks((previousState) => {
        previousState.mousePosition.value = `X=${Math.round(
          clientX
        )} Y=${Math.round(clientY)}`;
        return previousState;
      });
      forceUpdate();
    }, 200);

    addEventListener('touchmove', touchMoveHandler);
    addEventListener('mousemove', mouseMoveHandler);

    return () => {
      removeEventListener('touchmove', touchMoveHandler);
      removeEventListener('mousemove', mouseMoveHandler);
    };
  }, []);

  useEffect(() => {
    if (!navigator.getBattery) return;

    navigator.getBattery().then((battery) => {
      const batteryChangeHandler = () => {
        setDataBlocks((previousState) => {
          previousState.batteryChargingStatus.value = battery.charging;
          previousState.batteryLevel.value = battery.level * 100;
          return previousState;
        });
        forceUpdate();
      };

      batteryChangeHandler();

      battery.addEventListener('chargingchange', () => {
        batteryChangeHandler();
      });
    });
  }, []);

  return {
    loading: loadingCount > 0,
    dataBlocks,
  };
};
